import { Routes } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { noAuthGuard } from './core/auth/guards/noAuth.guard';
import { noAuthMatch } from './core/auth/guards/noAuth.match';
import { authGuard } from './core/auth/guards/auth.guard';
import { authMatch } from './core/auth/guards/auth.match';
import { tenantGuard } from './core/auth/guards/tenant.guard';
import { mobileViewRedirectGuard } from './core/services/mobile-view.guard';
import { posPwaRedirectGuard } from './core/guards/pos-pwa-redirect.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LayoutComponent,
    canMatch: [noAuthMatch],
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    children: [
      {
        path: 'login',
        data: {
          title: 'Auth.Login.Title',
        },
        loadComponent: () =>
          import('./modules/auth/login/login.component').then((c) => c.LoginComponent),
      },
      {
        path: 'auth/mfa-verify',
        data: {
          title: 'Auth.MfaVerify.Title',
        },
        loadComponent: () =>
          import('./modules/auth/mfa-verify/mfa-verify.component').then(
            (m) => m.MfaVerifyComponent,
          ),
      },
      {
        path: 'auth/mfa-setup',
        data: {
          title: 'Auth.MfaSetup.Title',
        },
        loadComponent: () =>
          import('./modules/auth/mfa-setup/mfa-setup.component').then((m) => m.MfaSetupComponent),
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    canMatch: [authMatch],
    canActivate: [authGuard, mobileViewRedirectGuard],
    canActivateChild: [authGuard, tenantGuard],
    children: [
      // Redirect guest-only paths to /profile when user is already authenticated
      { path: 'login', redirectTo: '/profile', pathMatch: 'full' },
      { path: 'auth/mfa-verify', redirectTo: '/profile', pathMatch: 'full' },
      { path: 'auth/mfa-setup', redirectTo: '/profile', pathMatch: 'full' },
      {
        path: 'pos',
        data: {
          title: 'StoreManagement.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/pos/pos.routes').then((m) => m.routes),
      },
      {
        path: 'sales',
        loadChildren: () => import('./modules/sales/sales.routes').then((m) => m.routes),
      },
      {
        path: 'test-center',
        data: {
          title: 'Navigation.TestCenter',
          resourceUniqueCode: 'TEST-CENTER',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/test-center/test-center.routes').then((m) => m.routes),
      },
      {
        path: 'crm',
        data: {
          title: 'CRM.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/crm/crm.routes').then((m) => m.routes),
      },
      {
        path: 'procurement',
        data: {
          title: 'Navigation.Procurement',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/procurement/procurement.routes').then((m) => m.routes),
      },
      {
        path: 'planning',
        data: {
          title: 'Navigation.Planning',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/planning/planning.routes').then((m) => m.routes),
      },
      {
        path: 'manufacturing',
        data: {
          title: 'ManufactureManagement.Title',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/manufacturing/manufacturing.routes').then((m) => m.routes),
      },
      {
        path: 'profile',
        data: {
          title: 'Navigation.Profile',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/profile/profile.routes').then((m) => m.routes),
      },
      {
        path: 'administration',
        data: {
          title: 'Navigation.Administration',
          resourceUniqueCode: 'AC',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/administration/administration.routes').then((m) => m.routes),
      },
      {
        path: 'security',
        data: {
          title: 'Navigation.Security',
          resourceUniqueCode: 'SEC',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/security/security.routes').then((m) => m.routes),
      },
      {
        path: 'platform',
        data: {
          title: 'Navigation.Platform',
          resourceUniqueCode: 'PLAT',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/platform/platform.routes').then((m) => m.routes),
      },
      {
        path: 'governance',
        data: {
          title: 'Navigation.Governance',
          resourceUniqueCode: 'GOV',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/governance/governance.routes').then((m) => m.routes),
      },
      {
        path: 'warehouse',
        data: {
          title: 'WarehouseManagement.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/warehouse/warehouse.routes').then((m) => m.routes),
      },
      {
        path: 'logistics',
        data: {
          title: 'Navigation.LogisticManagement',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/logistics/logistics.routes').then((m) => m.routes),
      },
      // logistic-management merged into logistics/freight-forwarding (Sprint R-H)
      {
        path: 'project-management',
        data: {
          title: 'Project.Title',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/project-management/project-management.routes').then((m) => m.routes),
      },
      {
        path: 'finance',
        data: {
          title: 'GeneralLedger.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/finance/finance.routes').then((m) => m.routes),
      },
      {
        path: 'hrm',
        data: {
          title: 'HRM.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/hrm/hrm.routes').then((m) => m.routes),
      },
      {
        path: 'employee',
        data: {
          title: 'Employee.Title',
          layout: 'admin',
        },
        loadComponent: () =>
          import('./modules/hrm/employees/employee.component').then((m) => m.EmployeeComponent),
      },
      {
        path: 'employee/:id',
        data: {
          title: 'Employee.EmployeeDetail',
          layout: 'blank',
        },
        loadComponent: () =>
          import('./modules/hrm/employees/employee-detail/employee-detail.component').then(
            (m) => m.EmployeeDetailComponent,
          ),
      },
      {
        path: 'asset-management',
        data: {
          title: 'AssetManagement.Title',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/asset-management/asset-management.routes').then((m) => m.routes),
      },
      {
        path: 'fusion',
        data: {
          title: 'Fusion.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/fusion/fusion.routes').then((m) => m.routes),
      },
      {
        path: 'pricing',
        data: {
          title: 'PRICING.TITLE',
          resourceUniqueCode: 'PRICING',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/pricing/pricing.routes').then((m) => m.routes),
      },
      {
        path: 'master-data',
        data: {
          title: 'MasterData.Title',
          resourceUniqueCode: 'MD',
          layout: 'admin',
        },
        loadChildren: () =>
          import('./modules/master-data/master-data.routes').then((m) => m.routes),
      },
      {
        path: 'okr-kpi',
        data: {
          title: 'OKR_KPI.Title',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/okr-kpi/okr-kpi.routes').then((m) => m.routes),
      },
      {
        path: 'risk',
        data: {
          title: 'Navigation.RiskManagement',
          resourceUniqueCode: 'RISK',
          layout: 'admin',
        },
        loadChildren: () => import('./modules/risk/risk.routes').then((m) => m.routes),
      },
      {
        path: 'notifications',
        data: { title: 'Notification.Center.Title', layout: 'admin' },
        loadChildren: () =>
          import('./modules/notification/notification.routes').then((m) => m.routes),
      },
      {
        path: 'knowledge',
        data: { title: 'Knowledge.Title', layout: 'admin' },
        loadChildren: () =>
          import('./modules/knowledge-hub/knowledge-hub.routes').then((m) => m.routes),
      },
      {
        path: 'component-library',
        data: {
          title: 'Navigation.ComponentLibrary',
          layout: 'admin',
          resourceUniqueCode: 'COMP-LIB',
        },
        loadChildren: () =>
          import('./modules/hub/component-library/component-library.routes').then((m) => m.routes),
      },
    ],
  },
  {
    // Carot ERP Mobile — giao diện mobile tối ưu (PWA standalone ưu tiên mở bản này;
    // chuyển đổi 2 chiều với bản desktop qua ViewModeService, ghi nhớ lựa chọn).
    path: 'mobile',
    canActivate: [authGuard],
    data: {
      title: 'Mobile.Title',
      layout: 'blank',
    },
    loadChildren: () => import('./modules/mobile/mobile.routes').then((m) => m.MOBILE_ROUTES),
  },
  {
    path: 'wms-mobile',
    canActivate: [authGuard],
    data: {
      title: 'WMS.Mobile.Title',
      layout: 'blank',
      // US-WH-07: app kho di động ghi tồn thật (nhận hàng, nhặt, kiểm đếm, xuất nhanh) — trước
      // đây mọi tài khoản đã đăng nhập đều mở được vì route không khai mã tài nguyên.
      resourceUniqueCode: 'WM-MOB',
    },
    loadChildren: () =>
      import('./modules/wms-mobile/wms-mobile.routes').then((m) => m.WMS_MOBILE_ROUTES),
  },
  {
    // M8 (Alex 04/11): app POS di động cũ đã gỡ — POS di động là PWA riêng ở /pos-m/.
    // Giữ đường dẫn cũ để link đã ghim nhảy sang PWA thay vì 404 (posPwaRedirectGuard).
    path: 'pos-mobile-app',
    canActivate: [posPwaRedirectGuard],
    children: [{ path: '**', children: [] }],
  },
  {
    path: 'not-authorized',
    data: {
      title: 'NotFound.NotAuth',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/not-authorized/not-authorized.component').then(
        (m) => m.NotAuthorizedComponent,
      ),
  },
  {
    path: 'server-error',
    data: {
      title: 'ErrorPages.ServerError',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/server-error/server-error.component').then((m) => m.ServerErrorComponent),
  },
  {
    // Non-admin lands here when the system has no tenant. OUTSIDE the authenticated shell on
    // purpose: no tenantGuard runs, so it cannot redirect-loop. Auth is still required.
    path: 'system-not-initialized',
    canActivate: [authGuard],
    data: {
      title: 'ErrorPages.SystemNotInitialized',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/administration/tenant/system-not-initialized/system-not-initialized.component').then(
        (m) => m.SystemNotInitializedComponent,
      ),
  },
  {
    // Post-login welcome for an admin when no tenant exists yet. OUTSIDE the shell (blank
    // layout) so tenantGuard never runs on it. The component self-corrects if a tenant
    // exists (→ app) or the user is a non-admin (→ system-not-initialized).
    path: 'onboarding',
    canActivate: [authGuard],
    data: {
      title: 'Onboarding.WelcomeTitle',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
  },
  {
    path: 'maintenance',
    data: {
      title: 'Maintenance.SystemUnderMaintenance',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
  },
  {
    path: 'not-found',
    data: {
      title: 'NotFound.PageNotFound',
      layout: 'blank',
    },
    loadComponent: () =>
      import('./modules/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
  {
    path: '**',
    redirectTo: 'not-found',
    pathMatch: 'full',
  },
];
